import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import {
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  type User as FirebaseUser,
} from 'firebase/auth';
import { auth } from './firebase';
import { apiClient } from './api-client';
import { appSurface, maskProducts, surfaceAccessFor, type SurfaceAccess } from './app-surface';

export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: 'participant' | 'admin' | 'project_manager' | 'qa' | 'super_admin' | 'recruiter' | 'scheduler';
  tenantName?: string;
  email_verified?: boolean;
  created_at?: string;
  phone?: string | null;
  timezone?: string | null;
  idv_status?: string;
  profile?: {
    gender?: string;
    age_range?: string;
    accent_label?: string;
    preferred_environment?: string;
  } | null;
  isTenantExpired?: boolean;
  tenantAccessMode?: 'active' | 'expired_read_only';
  tenantGraceDaysRemaining?: number;
  tenantExpiredAt?: string;
  tenantGraceEndsAt?: string;
  products?: {
    data_collection: boolean;
    red_team: boolean;
    appbench: boolean;
    physical_ai?: boolean;
    promptbench?: boolean;
    study_scheduling?: boolean;
    agents?: boolean;
  };
  /** The tenant's products before this app hid the other app's modules. */
  allProducts?: User['products'];
  /** Whether this user may use this app (Data Collection or Orca). */
  surfaceAccess?: SurfaceAccess;
}

interface AuthContextValue {
  user: User | null;
  firebaseUser: FirebaseUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<User | null>;
}

let activeProfileRequest: Promise<User> | null = null;

function fetchProfileWithRetry(): Promise<User> {
  if (activeProfileRequest) return activeProfileRequest;

  activeProfileRequest = (async () => {
    const retryDelays = [0, 1000, 2000, 4000];
    let lastError: unknown;

    for (const delay of retryDelays) {
      if (delay > 0) await new Promise((resolve) => window.setTimeout(resolve, delay));
      try {
        const response = await apiClient.get<User>('/participants/me');
        return forThisApp(response.data);
      } catch (error: any) {
        lastError = error;
        const status = Number(error?.response?.status || 0);
        if (status > 0 && status < 500) throw error;
      }
    }

    throw lastError;
  })().finally(() => {
    activeProfileRequest = null;
  });

  return activeProfileRequest;
}

/** Hides the other app's modules and records whether this app is available to the user. */
export function forThisApp(profile: User, surface = appSurface): User {
  const allProducts = profile.products;
  return {
    ...profile,
    allProducts,
    surfaceAccess: surfaceAccessFor(profile, surface),
    ...(allProducts || surface !== 'all' ? { products: maskProducts(allProducts, surface) as User['products'] } : {}),
  };
}

function isDefinitiveAccessRejection(error: unknown): boolean {
  const status = Number((error as { response?: { status?: number } })?.response?.status || 0);
  return status === 401 || status === 403;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Listen to Firebase auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      setFirebaseUser(fbUser);

      if (fbUser) {
        try {
          // Firebase user is signed in — fetch app-level profile from backend
          setUser(await fetchProfileWithRetry());
        } catch (error) {
          // User exists in Firebase but not in our DB (or token issue)
          setUser(null);
          if (isDefinitiveAccessRejection(error)) {
            await firebaseSignOut(auth).catch(() => undefined);
            setFirebaseUser(null);
          }
        }
      } else {
        // Signed out
        setUser(null);
      }

      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    // Firebase handles the password verification
    const credential = await signInWithEmailAndPassword(auth, email, password);

    try {
      // Atlas validates account and tenant access after Firebase verifies the
      // password. A rejected tenant must not leave a Firebase session behind.
      setUser(await fetchProfileWithRetry());
      setFirebaseUser(credential.user);
    } catch (error) {
      setUser(null);
      setFirebaseUser(null);
      if (isDefinitiveAccessRejection(error)) {
        await firebaseSignOut(auth).catch(() => undefined);
      }
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    await firebaseSignOut(auth);
    setUser(null);
    setFirebaseUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    if (!auth.currentUser) {
      setUser(null);
      return null;
    }
    await auth.currentUser.getIdToken(true);
    const profile = await fetchProfileWithRetry();
    setUser(profile);
    setFirebaseUser(auth.currentUser);
    return profile;
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        firebaseUser,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
